import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Barbell, Check, X, ArrowRight, Lightning, Quotes, Lock, Scales, Brain } from "@phosphor-icons/react";

const Wordmark = ({ size = "xl" }) => {
  const cls = size === "xl"
    ? "font-oswald text-2xl font-bold uppercase tracking-widest"
    : "font-oswald text-lg font-bold uppercase tracking-wider";
  const supCls = size === "xl"
    ? "font-manrope text-xs font-semibold tracking-normal ml-1 -top-2 relative"
    : "font-manrope text-[11px] font-semibold tracking-normal ml-0.5 -top-1.5 relative";
  return (
    <span className={cls}>
      Lazy Lean Method<sup className={supCls}>™</sup>
    </span>
  );
};

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [price] = useState(89); // weekly instalment price on payment plan
  const fullPrice = 997;
  const weeks = 12;
  const totalPlan = price * weeks;
  const savings = totalPlan - fullPrice;
  const [currency] = useState("AUD");
  const [scrolled, setScrolled] = useState(false);

  // Founder Member scarcity — pricing only, not spot count.
  // Regular $89/wk → Founder $59/wk (Stripe promo code FOUNDER1 = $30 off/wk).
  const FOUNDER_WEEKLY = 59;
  const founderWeeklySavings = price - FOUNDER_WEEKLY;
  const founderTotalSavings = founderWeeklySavings * weeks;
  const [founderOpen, setFounderOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Poll public founder stats — only used to know if spots remain (not shown).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get("/founders/stats");
        setFounderOpen(Number(data?.remaining || 0) > 0);
      } catch (e) { /* silent — hide badge on failure */ }
    })();
  }, []);

  const go = () => {
    if (user) navigate(user.role === "coach" ? "/coach" : "/app");
    else navigate("/register");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white id-grid-bg">
      {/* Sticky nav */}
      <header className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-200 ${scrolled ? "bg-[#0A0A0A]/95 backdrop-blur border-b border-[#262626]" : ""}`}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" data-testid="landing-logo" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#FF3333] flex items-center justify-center">
              <Barbell weight="bold" size={18} color="#fff" />
            </div>
            <Wordmark size="sm" />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="landing-login-link" className="id-btn-ghost text-xs">Sign in</Link>
            <button onClick={go} data-testid="landing-nav-cta" className="id-btn-primary text-xs">
              Start — ${price}/wk
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-40 pb-24 px-6 overflow-hidden">
        <div className="absolute inset-0 id-stripes pointer-events-none opacity-50" />
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-center relative">
          <div className="lg:col-span-7">
            <div className="id-label text-[#FF3333] mb-4">— FOR MEN WHO HATE DIETS —</div>
            <h1 className="font-oswald text-5xl md:text-7xl font-bold uppercase tracking-tight leading-[0.95]">
              Lose your<br/>
              first <span className="text-[#FF3333]">10 kg</span><br/>
              <span className="text-white">without counting</span><br/>
              <span className="text-zinc-500">a single calorie.</span>
            </h1>
            <p className="text-zinc-300 font-manrope text-lg mt-8 max-w-xl leading-relaxed">
              No "summer cuts." No bro splits. No meal-plan PDFs you'll never follow.
              Just <span className="text-white font-semibold">The 7 Lazy Lean Habits™</span>, a custom plan built around your life,
              a coach who actually answers your messages, and a brotherhood that won't let you ghost yourself.
            </p>

            {founderOpen && (
              <div
                className="mt-8 max-w-xl border border-[#FF3333]/60 bg-gradient-to-br from-[#FF3333]/10 to-transparent p-5"
                data-testid="founder-pricing-card"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Lightning size={14} weight="fill" className="text-[#FF3333]" />
                  <div className="id-label text-[#FF3333]">— Founder Member Pricing · Active Now —</div>
                </div>
                <div className="flex items-baseline gap-3 mb-2">
                  <span className="font-oswald text-5xl font-bold leading-none text-white" data-testid="founder-rate">
                    ${FOUNDER_WEEKLY}
                  </span>
                  <span className="text-zinc-400 text-lg font-manrope">/week</span>
                  <span className="text-zinc-500 text-base line-through font-manrope">${price}/wk</span>
                </div>
                <p className="text-zinc-300 font-manrope text-sm leading-relaxed" data-testid="founder-savings">
                  Start today as a <span className="text-white font-semibold">Founder Member</span> and save{" "}
                  <span className="text-[#FF3333] font-semibold">${founderTotalSavings}</span> across your 12-week program
                  <span className="text-zinc-500"> · ${founderWeeklySavings}/wk off, locked in for the full program</span>.
                </p>
                <p className="text-zinc-500 font-manrope text-xs mt-3 uppercase tracking-[0.15em]">
                  Enter code <span className="text-white font-semibold tracking-[0.2em]">FOUNDER1</span> at checkout
                </p>
              </div>
            )}

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button onClick={go} data-testid="hero-cta-button" className="id-btn-primary text-base">
                Start now — ${price} / week <ArrowRight size={16} weight="bold" />
              </button>
              <Link to="/login" data-testid="hero-secondary-link" className="id-btn-secondary text-base">
                Existing member? Sign in
              </Link>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-zinc-500 font-manrope uppercase tracking-[0.2em]">
              <span className="flex items-center gap-2"><Lock size={12} weight="bold" /> 12-week program</span>
              <span className="flex items-center gap-2"><Lightning size={12} weight="fill" className="text-[#FF3333]" /> Access in 60 seconds</span>
              <span>{currency} · pay-as-you-go</span>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] bg-cover bg-center border border-[#262626]"
                 style={{ backgroundImage: 'url(/jimmy-after.png)' }}
                 data-testid="hero-image">
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="id-label text-[#FF3333] mb-2">— Coach Jimmy —</div>
                <div className="font-oswald text-2xl md:text-3xl uppercase tracking-wide leading-tight">
                  Master the habits.<br/>
                  Watch the scale move.
                </div>
              </div>
            </div>
            {/* Floating price tag */}
            <div className="hidden lg:flex absolute -top-6 -left-6 bg-[#FF3333] px-5 py-4 items-baseline gap-1 shadow-2xl">
              <span className="font-oswald text-4xl font-bold leading-none">${price}</span>
              <span className="text-white/80 text-sm font-manrope">/wk</span>
            </div>
          </div>
        </div>
      </section>

      {/* Meet your coach — Before / After */}
      <section className="py-20 px-6 border-t border-[#262626] bg-[#0A0A0A]" data-testid="section-meet-coach">
        <div className="max-w-6xl mx-auto">
          <div className="id-label mb-4 text-[#FF3333]">— Meet your coach —</div>
          <h2 className="id-h2 max-w-3xl mb-3">
            I&apos;m Jimmy. I&apos;ve been exactly where you are.
          </h2>
          <p className="text-zinc-400 font-manrope text-lg max-w-2xl mb-12">
            Not a 22-year-old fitness influencer. Just a dad who got tired of starting over every Monday — figured out the 7 habits that actually work, and built the system I wish I&apos;d had.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {/* BEFORE */}
            <div className="relative group" data-testid="coach-before">
              <div className="relative aspect-[4/5] overflow-hidden border border-[#262626] bg-[#141414]">
                <img
                  src="/jimmy-before.jpg"
                  alt="Jimmy before — exhausted dad holding his sleeping daughter"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
                <div className="absolute top-4 left-4 bg-[#0A0A0A]/85 border border-zinc-700 px-3 py-1.5 backdrop-blur-sm">
                  <div className="id-label text-zinc-400 text-[10px]">— Before —</div>
                </div>
                <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent">
                  <div className="font-oswald text-xl uppercase tracking-wide text-white leading-tight">
                    Knackered. Soft.<br/>Hiding from photos.
                  </div>
                  <p className="text-zinc-400 text-sm font-manrope mt-2 leading-snug">
                    Up at night, no energy, snapping at the family.
                    Telling myself I&apos;d &quot;sort it out soon.&quot;
                  </p>
                </div>
              </div>
            </div>

            {/* AFTER */}
            <div className="relative group" data-testid="coach-after">
              <div className="relative aspect-[4/5] overflow-hidden border border-[#FF3333] bg-[#141414]">
                <img
                  src="/jimmy-after.png"
                  alt="Jimmy after — lean, strong, dialled in"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
                <div className="absolute top-4 left-4 bg-[#FF3333] px-3 py-1.5">
                  <div className="id-label text-white text-[10px]">— After —</div>
                </div>
                <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent">
                  <div className="font-oswald text-xl uppercase tracking-wide text-white leading-tight">
                    7 Lazy Lean Habits.<br/>That&apos;s it.
                  </div>
                  <p className="text-zinc-300 text-sm font-manrope mt-2 leading-snug">
                    No calorie counting. No 6am cardio. No giving up the
                    beers entirely. Just consistency on the things that matter.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 max-w-3xl">
            <p className="text-zinc-300 font-manrope text-lg leading-relaxed">
              If you&apos;re looking at that first photo and thinking <span className="text-white font-semibold">&quot;that&apos;s me right now&quot;</span> — you&apos;re in the right place. I built Lazy Lean Method because I needed it. Now I run a few dozen blokes through it every week.
            </p>
            <button
              type="button"
              onClick={() => navigate("/register")}
              className="id-btn-primary mt-6 px-8 py-4 text-sm flex items-center gap-2"
              data-testid="meet-coach-cta"
            >
              Start the method · ${price}/wk
              <ArrowRight size={16} weight="bold" />
            </button>
          </div>
        </div>
      </section>

      {/* Who this is for */}
      <section className="py-20 px-6 border-t border-[#262626] bg-[#0F0F0F]" data-testid="section-who-its-for">
        <div className="max-w-6xl mx-auto">
          <div className="id-label mb-4 text-[#FF3333]">— Who this is for —</div>
          <h2 className="id-h2 max-w-3xl mb-3">
            If you're a man carrying some weight, busy, a dad, or just looking for a <span className="text-[#FF3333]">brotherhood</span> — you're home.
          </h2>
          <p className="text-zinc-400 font-manrope text-lg max-w-2xl mb-12">
            One thing in common: you're done winging it on your own. You want a system, accountability, and a few good men in the trenches with you.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { t: "Carrying some weight", b: "First 10 kg, last 20 kg, or somewhere in between — we start where you are." },
              { t: "Busy as hell", b: "Long hours, shift work, travel — your plan bends around it instead of breaking you." },
              { t: "Done with the bullshit", b: "Sick of not following through. Tired of diets that don't fit your life. Ready for something that actually sticks past Monday." },
              { t: "Want a brotherhood", b: "Other men chasing the same thing — coach in the corner, blokes around you." },
            ].map((p, i) => (
              <div key={i} className="border border-[#262626] p-5 hover:border-[#FF3333] transition-colors" data-testid={`who-card-${i}`}>
                <div className="font-oswald text-3xl text-[#FF3333] font-bold mb-3">0{i + 1}</div>
                <div className="font-oswald text-lg uppercase tracking-wide mb-2">{p.t}</div>
                <p className="text-zinc-400 font-manrope text-sm leading-snug">{p.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why most diets fail */}
      <section className="py-24 px-6 border-t border-[#262626]" data-testid="section-problem">
        <div className="max-w-6xl mx-auto">
          <div className="id-label mb-4">— Why other plans failed you —</div>
          <h2 className="id-h2 max-w-3xl mb-12">You don't have a willpower problem. You have a <span className="text-[#FF3333]">system</span> problem.</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#262626] border border-[#262626]">
            {[
              { t: "Calorie counting", b: "Works for 11 days. Crashes on day 12 at a restaurant." },
              { t: "Generic gym splits", b: "Built for 22-year-olds with nothing else on. Not you." },
              { t: "Crash diets", b: "Lose 4kg, gain 6kg back, hate yourself for it." },
            ].map((c, i) => (
              <div key={i} className="bg-[#0A0A0A] p-8">
                <X size={28} weight="bold" className="text-[#FF3333] mb-4" />
                <div className="font-oswald text-xl uppercase tracking-wide mb-2">{c.t}</div>
                <p className="text-zinc-400 font-manrope text-sm">{c.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The method */}
      <section className="py-24 px-6 border-t border-[#262626]" data-testid="section-method">
        <div className="max-w-6xl mx-auto">
          <div className="id-label mb-4">— The Method —</div>
          <h2 className="id-h2 max-w-3xl mb-3">The 7 Lazy Lean Habits™. <span className="text-[#FF3333]">That's it.</span></h2>
          <p className="text-zinc-400 font-manrope text-lg max-w-2xl mb-12">
            You tick 7 boxes each day. Your coach builds the workouts around your schedule, your gym, your injuries. The portal does the tracking. You just show up.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { n: "01", e: "🥩", t: "Protein First", sub: "At every main meal." },
              { n: "02", e: "🚶", t: "10,000 Steps Daily", sub: "Move every day." },
              { n: "03", e: "💧", t: "Drink Your Water", sub: "2.5–3L daily." },
              { n: "04", e: "🥦", t: "Build Better Plates", sub: "½ protein & veg · ¼ carbs · ¼ whatever." },
              { n: "05", e: "🌙", t: "Kitchen Closed After Dinner", sub: "No mindless snacking." },
              { n: "06", e: "🪞", t: "Identity Check", sub: "1 min in the mirror. State who you're becoming." },
              { n: "07", e: "😴", t: "Sleep — 6 Hours Min", sub: "Log your hours. Weekly total shown." },
            ].map((h, i) => (
              <div key={i} className="border border-[#262626] p-5 hover:border-[#FF3333] transition-colors" data-testid={`rule-card-${i}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="font-oswald text-3xl text-[#FF3333] font-bold">{h.n}</div>
                  <div className="text-3xl leading-none">{h.e}</div>
                </div>
                <div className="font-oswald text-lg uppercase tracking-wide text-white leading-tight mb-1">{h.t}</div>
                <div className="font-manrope text-sm text-zinc-400 leading-snug">{h.sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Feature icon={Scales} title="Habit-first" body="Habits don't break at restaurants. Calorie apps do." />
            <Feature icon={Brain} title="Built for real life" body="Plans that bend around long days, kids, shift work and whatever else you've got on." />
            <Feature icon={Lightning} title="Coach access" body="Direct line to me — not a chatbot, not a forum." />
          </div>
        </div>
      </section>

      {/* What you get */}
      <section className="py-24 px-6 border-t border-[#262626] bg-[#0F0F0F]" data-testid="section-features">
        <div className="max-w-6xl mx-auto">
          <div className="id-label mb-4">— Inside the portal —</div>
          <h2 className="id-h2 max-w-3xl mb-12">Everything you need, <span className="text-[#FF3333]">nothing you don't.</span></h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { t: "Your own custom plan", b: "Workouts built for you specifically — not a PDF template downloaded for 10,000 people." },
              { t: "The 7 Habits, tracked daily", b: "Tick them off in 10 seconds. Get your weekly score out of 49. Watch your body change." },
              { t: "Body weight + chart", b: "Log your weight, see the trend. No fancy bio-impedance scales required." },
              { t: "Direct coach chat", b: "Stuck? Confused? Need a swap? Message me from inside the portal, get an answer." },
              { t: "Per-exercise notes", b: "Coaching cues, tempo, swaps — written next to each exercise, not buried in a PDF." },
              { t: "Pay as you go", b: "No 12-month contracts. No 'cancel anytime' that takes 3 emails. Stop paying = stop the service." },
            ].map((f, i) => (
              <div key={i} className="flex items-start gap-4 p-6 border border-[#262626] bg-[#0A0A0A]">
                <Check size={22} weight="bold" className="text-[#FF3333] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-oswald text-lg uppercase tracking-wide mb-1">{f.t}</div>
                  <p className="text-zinc-400 font-manrope text-sm leading-relaxed">{f.b}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-24 px-6 border-t border-[#262626]" data-testid="section-quote">
        <div className="max-w-3xl mx-auto text-center">
          <Quotes size={48} weight="fill" className="text-[#FF3333] mx-auto" />
          <p className="font-oswald text-2xl md:text-4xl uppercase tracking-wide leading-tight mt-6">
            "Lost 11 kg in 14 weeks.<br/>
            <span className="text-[#FF3333]">Never tracked a calorie.</span>"
          </p>
          <div className="text-zinc-500 font-manrope text-sm mt-6 uppercase tracking-[0.2em]">— Real client · name withheld</div>
        </div>
      </section>

      {/* Pricing CTA */}
      <section className="py-24 px-6 border-t border-[#262626] bg-[#0F0F0F]" data-testid="section-pricing">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <div className="id-label mb-4 text-[#FF3333]">— The Lazy Lean Method™ —</div>
            <h2 className="id-h1 mb-3">12-Week Coaching Program</h2>
            <p className="text-zinc-400 font-manrope max-w-xl mx-auto">
              Pick your payment option and you&apos;re in. Same program either way.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Pay in Full */}
            <div className="relative id-card border-[#FF3333] shadow-[inset_0_0_60px_rgba(255,51,51,0.08)]" data-testid="landing-plan-full">
              <div className="absolute -top-3 left-6 bg-[#FF3333] text-white px-3 py-1 font-oswald text-[10px] uppercase tracking-[0.2em] font-bold">
                ⭐ Best Value — Pay in Full
              </div>
              <div className="id-label mt-2 mb-2">One-Time Payment</div>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="font-oswald text-6xl font-bold text-white">${fullPrice}</span>
                <span className="text-zinc-400 font-manrope text-lg">AUD</span>
              </div>
              <div className="text-xs text-zinc-500 font-manrope mb-6">One payment, no ongoing billing</div>
              <ul className="space-y-3 mb-6">
                {[
                  `Save $${savings} compared to the payment plan`,
                  "One payment, no ongoing billing",
                  "Full access to the complete 12-week coaching system",
                ].map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-zinc-200 font-manrope">
                    <Check size={18} weight="bold" className="text-[#FF3333] flex-shrink-0 mt-0.5" />
                    {b}
                  </li>
                ))}
              </ul>
              <button onClick={go} data-testid="landing-cta-full" className="id-btn-primary w-full text-base">
                Pay in Full — ${fullPrice} <ArrowRight size={16} weight="bold" />
              </button>
            </div>

            {/* Payment Plan */}
            <div className="id-card" data-testid="landing-plan-plan">
              <div className="id-label mt-2 mb-2">Flexible Payment Plan</div>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="font-oswald text-6xl font-bold text-white">${price}</span>
                <span className="text-zinc-400 font-manrope text-lg">/ week</span>
              </div>
              <div className="text-xs text-zinc-500 font-manrope mb-6">{weeks} weekly payments · Total ${totalPlan} AUD</div>
              <ul className="space-y-3 mb-6">
                {[
                  "Same coaching program",
                  "Same support",
                  `Spread the investment over ${weeks} weeks`,
                ].map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-zinc-200 font-manrope">
                    <Check size={18} weight="bold" className="text-[#FF3333] flex-shrink-0 mt-0.5" />
                    {b}
                  </li>
                ))}
              </ul>
              <button onClick={go} data-testid="landing-cta-plan" className="id-btn-secondary w-full text-base">
                Start Payment Plan — ${price}/wk <ArrowRight size={16} weight="bold" />
              </button>
            </div>
          </div>

          <div className="text-center mt-10">
            <div className="text-xs text-zinc-500 font-manrope uppercase tracking-[0.2em]">
              <Link to="/login" data-testid="pricing-signin-link" className="hover:text-white">Already a member? Sign in</Link>
            </div>
          </div>

          {/* Week 13+ Continued Coaching row */}
          <div
            className="mt-10 border border-[#262626] bg-[#0A0A0A] px-6 py-6 flex flex-col md:flex-row md:items-center gap-4"
            data-testid="landing-continued-row"
          >
            <div className="flex-1 min-w-0">
              <div className="id-label text-[#FF3333] mb-1.5">— Week 13+ · Continued Coaching —</div>
              <div className="font-oswald text-2xl md:text-3xl uppercase tracking-wide text-white leading-tight">
                Keep going for <span className="text-[#FF3333]">$69/week</span>
              </div>
              <p className="text-zinc-400 font-manrope text-sm mt-2 leading-relaxed max-w-2xl">
                After your 12 weeks are done you&apos;ll be fitter, leaner and know the system inside out. Keep the coach, chat, habit tracker and dashboard for a reduced $69/wk. Payment Plan rolls over automatically · Pay in Full opts in when ready · Cancel anytime.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-6 border-t border-[#262626]" data-testid="section-faq">
        <div className="max-w-3xl mx-auto">
          <div className="id-label mb-4">— Questions —</div>
          <h2 className="id-h2 mb-8">Stuff you're probably wondering</h2>
          <div className="space-y-4">
            {[
              { q: "Do I need a gym?", a: "No. Your coach builds your plan around what equipment you have — home, hotel gym, or full commercial." },
              { q: "What if I miss a day?", a: "You miss a day. The streak resets, you start again. This is real life, not a video game." },
              { q: "How fast will I lose weight?", a: "Most clients see 1–2 kg in the first 2 weeks, then 0.5–1 kg/week after. Sustainable beats fast." },
              { q: "What if I want to stop?", a: "Stop paying. Access ends when your current week runs out. No emails to send, no calls to cancel." },
              { q: "How is this different from MyFitnessPal?", a: "MyFitnessPal is a tool you use. This is a coach + system + accountability. There's nothing to calculate." },
            ].map((f, i) => (
              <details key={i} className="border border-[#262626] bg-[#0A0A0A] group" data-testid={`faq-item-${i}`}>
                <summary className="px-5 py-4 cursor-pointer font-manrope font-semibold text-zinc-100 group-open:text-[#FF3333] transition-colors flex justify-between items-center">
                  {f.q}
                  <span className="text-[#FF3333] text-xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <div className="px-5 pb-5 text-zinc-400 font-manrope text-sm leading-relaxed">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#262626] py-10 px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[#FF3333] flex items-center justify-center">
              <Barbell weight="bold" size={14} color="#fff" />
            </div>
            <Wordmark size="sm" />
          </div>
          <div className="text-xs text-zinc-500 font-manrope uppercase tracking-[0.2em] flex gap-6">
            <Link to="/login" className="hover:text-white">Sign in</Link>
            <Link to="/register" className="hover:text-white">Sign up</Link>
          </div>
          <div className="text-xs text-zinc-600 font-manrope">© Lazy Lean Method™ · All rights reserved</div>
        </div>
      </footer>
    </div>
  );
}

function Feature({ icon: Icon, title, body }) {
  return (
    <div className="border border-[#262626] p-6">
      <Icon size={28} weight="bold" className="text-[#FF3333] mb-3" />
      <div className="font-oswald text-lg uppercase tracking-wide mb-1">{title}</div>
      <p className="text-zinc-400 font-manrope text-sm">{body}</p>
    </div>
  );
}
